import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, 
  FileText, 
  Wind, 
  Thermometer, 
  Snowflake, 
  Fan,
  Clock,
  Shield,
  Award,
  Users,
  CheckCircle,
  Star,
  MapPin,
  ChevronRight,
  Wrench,
  ArrowRight,
  Mail,
  ChevronLeft,
  Send,
  BadgeCheck,
  Building2,
  Home,
  Store
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Google 5 Stars Badge Component
const GoogleBadge = () => (
  <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-slate-100">
    <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="w-4 h-4 text-[#fabc63] fill-[#fabc63]" />
      ))}
    </div>
    <span className="text-xs font-medium text-slate-600">Avis clients</span>
  </div>
);

const HomePage = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  
  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    service_type: '',
    message: '',
    is_urgent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    {
      icon: Wind,
      title: "Climatisation",
      description: "Pose, dépannage et entretien de climatisation. Intervention rapide.",
      link: "/services#climatisation"
    },
    {
      icon: Thermometer,
      title: "Pompe à Chaleur",
      description: "Installation et dépannage PAC air/eau. Économies garanties.",
      link: "/services#pompe-a-chaleur"
    },
    {
      icon: Snowflake,
      title: "Froid Commercial",
      description: "Chambre froide, vitrine réfrigérée. Service professionnel.",
      link: "/services#froid-commercial"
    },
    {
      icon: Fan,
      title: "Ventilation",
      description: "VMC simple flux, double flux, réseau aéraulique.",
      link: "/services#ventilation"
    }
  ];

  const partners = [
    { name: "Daikin", logo: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/nv2dnvv2_Capture_d_e%CC%81cran_2026-03-16_a%CC%80_01.48.04-removebg-preview.png" },
    { name: "Mitsubishi Electric", logo: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/vljbejpr_Capture_d_e%CC%81cran_2026-03-16_a%CC%80_01.49.11-removebg-preview.png" },
    { name: "Atlantic", logo: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/y1detla1_Capture_d_e%CC%81cran_2026-03-16_a%CC%80_01.33.58-removebg-preview%20%281%29.png" },
    { name: "Toshiba", logo: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/783h68mr_Capture_d_e%CC%81cran_2026-03-16_a%CC%80_01.35.52-removebg-preview.png" },
    { name: "Panasonic", logo: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/dnvw4cog_Capture_d_e%CC%81cran_2026-03-16_a%CC%80_01.36.52-removebg-preview.png" },
    { name: "Hitachi", logo: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/5vu6gg1u_Capture_d_e%CC%81cran_2026-03-16_a%CC%80_01.37.50-removebg-preview.png" },
    { name: "LG", logo: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/cco6e9wi_Capture_d_e%CC%81cran_2026-03-16_a%CC%80_01.38.35-removebg-preview.png" },
    { name: "Carrier", logo: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/u1cxw907_Capture_d_e%CC%81cran_2026-03-16_a%CC%80_01.50.10-removebg-preview.png" }
  ];

  // 10 vrais avis clients
  const testimonials = [
    {
      name: "Oli Vier",
      location: "Seine-et-Marne",
      text: "Excellente expérience. Le technicien a pris le temps d'expliquer le fonctionnement. Devis clair, service rapide et de qualité. Je referai appel à eux sans hésiter.",
      rating: 5
    },
    {
      name: "Dekur Cv",
      location: "Moissy-Cramayel",
      text: "Installation de 4 climatiseurs impeccable, propre et soigné. Technicien ponctuel, très professionnel et de bon conseil. Je recommande vivement.",
      rating: 5
    },
    {
      name: "Younes Bakkar",
      location: "Melun",
      text: "Installation climatisation avec professionnalisme et efficacité. Travail réalisé avec soin et rapidité. Résultat parfait. Je recommande vivement.",
      rating: 5
    },
    {
      name: "Basta Baboune",
      location: "Ormesson-sur-Marne",
      text: "Dépannage climatiseurs en juillet. Problème trouvé en 2 heures alors que d'autres sociétés n'avaient pas réussi. Très professionnel.",
      rating: 5
    },
    {
      name: "Ala",
      location: "Seine-et-Marne",
      text: "Installation pompe à chaleur chez moi, très satisfait. Super soigneux et m'a très bien expliqué le fonctionnement. Je recommande vivement.",
      rating: 5
    },
    {
      name: "Liroy Dunand",
      location: "Seine-et-Marne",
      text: "Installation de 3 climatiseurs et VMC double flux. Travail très professionnel, propre et soigné. Équipe ponctuelle et sérieuse.",
      rating: 5
    },
    {
      name: "Loss Kib",
      location: "Seine-et-Marne",
      text: "Dépanneur ponctuel, professionnel et sympathique. Pompe à chaleur réparée rapidement, VMC vérifiée. Service complet et de qualité.",
      rating: 5
    },
    {
      name: "Ryless Benhaddad",
      location: "Seine-et-Marne",
      text: "Installation climatisation chez moi, absolument ravi du résultat. Je recommande.",
      rating: 5
    },
    {
      name: "Marvin Fautrai",
      location: "Seine-et-Marne",
      text: "Très bonne expérience et très professionnelle. Très bonne installation climatisation avec de très bons conseils. Merci à eux.",
      rating: 5
    },
    {
      name: "Anthony Londais",
      location: "Seine-et-Marne",
      text: "Super professionnel à l'écoute de la demande des clients. Je recommande cette entreprise, merci pour tout.",
      rating: 5
    }
  ];

  const stats = [
    { number: "13", label: "Ans d'expérience" },
    { number: "2000+", label: "Clients satisfaits" },
    { number: "24/7", label: "Disponibilité" },
    { number: "77", label: "Seine-et-Marne" }
  ];

  // FAQ SEO optimisée
  const faqs = [
    {
      question: "Intervenez-vous en urgence pour un dépannage de climatisation ?",
      answer: "Oui, ADM Confort Clim propose un service de dépannage urgent 24h/24 et 7j/7. Nous intervenons rapidement en Île-de-France et dans toute la France selon les besoins pour résoudre vos pannes de climatisation."
    },
    {
      question: "Travaillez-vous sur toutes les marques de climatisation ?",
      answer: "Oui, nos techniciens sont formés et certifiés pour intervenir sur toutes les grandes marques : Daikin, Mitsubishi Electric, Atlantic, Toshiba, Panasonic, Hitachi, LG et Carrier."
    },
    {
      question: "Installez-vous des chambres froides pour professionnels ?",
      answer: "Oui, nous réalisons le montage de chambres froides et vitrines réfrigérées pour les professionnels : restaurants, commerces, boucheries, boulangeries et tous métiers de bouche partout en France."
    },
    {
      question: "Réalisez-vous l'entretien de pompe à chaleur air/eau ?",
      answer: "Oui, nous assurons l'installation, le dépannage et l'entretien de pompes à chaleur air/eau. Un entretien régulier garantit les performances et la longévité de votre équipement."
    },
    {
      question: "Proposez-vous des solutions pour particuliers et professionnels ?",
      answer: "ADM Confort Clim intervient aussi bien chez les particuliers que chez les professionnels : commerces, restaurants, bureaux, entrepôts et entreprises. Nous adaptons nos solutions à vos besoins spécifiques."
    }
  ];

  const realisations = [
    {
      title: "Climatisation restaurant",
      location: "Seine-et-Marne (77)",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/s5eba2in_installation-climatisation-restaurant-plafond-commercial-seine-et-marne-adm-confort-clim.webp.jpeg"
    },
    {
      title: "Unités avec cache DecoClim",
      location: "Seine-et-Marne (77)",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/dspg48bw_WhatsApp%20Image%202026-03-15%20at%2022.05.57.jpeg"
    },
    {
      title: "Climatisation gainable boutique",
      location: "Seine-et-Marne (77)",
      image: "https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/t0krh7zc_installation-grille-soufflage-climatisation-commerce-seine-et-marne-adm-confort-clim.webp.jpeg"
    }
  ];

  // Testimonial carousel navigation
  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-scroll testimonials
  useEffect(() => {
    const interval = setInterval(nextTestimonial, 5000);
    return () => clearInterval(interval);
  }, []);

  // Contact form handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Message envoyé !', {
          description: 'Nous vous recontacterons rapidement.'
        });
        setFormData({
          name: '', email: '', phone: '', subject: '',
          service_type: '', message: '', is_urgent: false
        });
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi', {
        description: 'Appelez-nous au 06 03 05 49 11.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] lg:min-h-[90vh] flex items-center overflow-hidden bg-white" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1766788466565-768128d89ce4?w=1920&h=1080&fit=crop"
            alt="Installation climatisation Seine-et-Marne"
            className="w-full h-full object-cover"
          />
          <div className="hero-gradient absolute inset-0"></div>
        </div>

        <div className="container-custom relative z-10 py-12 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:pr-8"
            >
              {/* Trust badges row */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="inline-flex items-center gap-2 bg-[#fabc63] text-black px-4 py-2 rounded-full text-sm font-bold">
                  <Clock size={16} />
                  <span>Dépannage 24h/24 7j/7</span>
                </div>
                <GoogleBadge />
              </div>

              <h1 className="font-outfit font-bold text-[#000000] leading-[1.1] mb-4 lg:mb-6">
                <span className="block text-2xl sm:text-3xl lg:text-4xl xl:text-5xl">Climaticien & Frigoriste</span>
                <span className="block text-2xl sm:text-3xl lg:text-4xl xl:text-5xl mt-1">partout en <span className="text-[#1997b0]">France</span></span>
              </h1>

              <p className="text-base lg:text-lg text-slate-600 mb-6 lg:mb-8 max-w-lg">
                Installateur climatisation, pompe à chaleur et froid commercial. Basés en Seine-et-Marne, nous intervenons dans toute la France selon vos besoins.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <a 
                  href="tel:0603054911" 
                  className="btn-emergency flex items-center justify-center gap-2"
                  data-testid="hero-cta-call"
                >
                  <Phone size={20} />
                  <span>06 03 05 49 11</span>
                </a>
                <Link 
                  to="/contact" 
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="hero-cta-quote"
                >
                  <FileText size={20} />
                  <span>Devis gratuit</span>
                </Link>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center gap-4 lg:gap-6 pt-6 border-t border-slate-200/50">
                <div className="flex items-center gap-2">
                  <Shield className="text-[#1997b0]" size={18} />
                  <span className="text-sm text-slate-600">Garantie décennale</span>
                </div>
                <div className="flex items-center gap-2">
                  <BadgeCheck className="text-[#1997b0]" size={18} />
                  <span className="text-sm text-slate-600">Multi-marques</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="text-[#1997b0]" size={18} />
                  <span className="text-sm text-slate-600">13 ans d'expérience</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 lg:py-12 bg-[#1997b0]" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-outfit">{stat.number}</div>
                <div className="text-xs md:text-sm text-white/80 mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cibles Section */}
      <section className="py-10 bg-white" data-testid="targets-section">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-8">
            <div className="flex items-center gap-2 text-slate-600">
              <Home className="text-[#1997b0]" size={20} />
              <span className="text-sm font-medium">Particuliers</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Store className="text-[#1997b0]" size={20} />
              <span className="text-sm font-medium">Commerces</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Building2 className="text-[#1997b0]" size={20} />
              <span className="text-sm font-medium">Restaurants</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Building2 className="text-[#1997b0]" size={20} />
              <span className="text-sm font-medium">Bureaux</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Building2 className="text-[#1997b0]" size={20} />
              <span className="text-sm font-medium">Entreprises</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]" data-testid="services-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-10 lg:mb-14"
            {...fadeInUp}
          >
            <span className="badge-primary mb-3">Nos services</span>
            <h2 className="font-outfit text-2xl md:text-3xl lg:text-4xl font-bold text-[#000000] mt-3 mb-3">
              Climaticien et frigoriste basé en Seine-et-Marne
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-sm md:text-base">
              Pose, dépannage et entretien par des techniciens certifiés. Intervention rapide sur Daikin, Mitsubishi, Atlantic et toutes marques.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service group"
                data-testid={`service-card-${index}`}
              >
                <div className="service-icon mb-4">
                  <service.icon className="text-[#1997b0]" size={24} />
                </div>
                <h3 className="font-outfit text-base lg:text-lg font-semibold text-[#000000] mb-2">{service.title}</h3>
                <p className="text-slate-600 text-xs lg:text-sm mb-3">{service.description}</p>
                <Link 
                  to={service.link}
                  className="inline-flex items-center gap-1 text-[#1997b0] font-medium text-xs lg:text-sm"
                >
                  En savoir plus <ChevronRight size={14} />
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Emergency banner */}
          <motion.div 
            className="mt-10 lg:mt-12 bg-gradient-to-r from-[#fabc63]/10 to-[#fabc63]/5 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
            {...fadeInUp}
          >
            <div className="flex items-center gap-4 text-center sm:text-left">
              <div className="w-12 h-12 lg:w-14 lg:h-14 bg-[#fabc63] rounded-full flex items-center justify-center animate-pulse">
                <Wrench className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-outfit text-lg font-bold text-[#000000]">Urgence dépannage ?</h3>
                <p className="text-slate-600 text-sm">Intervention rapide 24h/24 - 7j/7 partout en France</p>
              </div>
            </div>
            <a 
              href="tel:0603054911"
              className="btn-emergency whitespace-nowrap"
              data-testid="emergency-banner-cta"
            >
              <Phone size={18} className="inline mr-2" />
              06 03 05 49 11
            </a>
          </motion.div>
        </div>
      </section>

      {/* About preview Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="about-preview-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="badge-primary mb-3">Entreprise locale</span>
              <h2 className="font-outfit text-2xl md:text-3xl lg:text-4xl font-bold text-[#000000] mt-3 mb-4">
                ADM Confort Clim, votre climaticien à Moissy-Cramayel
              </h2>
              <p className="text-slate-600 mb-6 text-sm md:text-base">
                Entreprise sérieuse basée en Seine-et-Marne, nous intervenons pour les particuliers et professionnels dans toute la France. Travail propre et soigné, accompagnement personnalisé.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Intervention rapide et disponibilité 24/7",
                  "Expertise multi-marques certifiée",
                  "Conseils personnalisés et devis gratuit"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700 text-sm">
                    <CheckCircle className="text-[#1997b0] flex-shrink-0" size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/a-propos"
                className="btn-secondary inline-flex items-center gap-2 text-sm"
                data-testid="about-cta"
              >
                En savoir plus <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_adm-confort-clim/artifacts/y4b9btoj_climatisation-maison-particulier-moissy-cramayel-seine-et-marne.jpg"
                alt="ADM Confort Clim - Camion et technicien climatisation Moissy-Cramayel Seine-et-Marne"
                className="w-full h-[300px] lg:h-[400px] object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-4 -left-4 lg:-bottom-6 lg:-left-6 bg-white p-4 lg:p-5 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 bg-[#1997b0] rounded-full flex items-center justify-center">
                    <Users className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="font-outfit text-xl lg:text-2xl font-bold text-[#000000]">2000+</div>
                    <div className="text-xs text-slate-600">Clients satisfaits</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-12 lg:py-16 bg-[#F8FAFC] overflow-hidden" data-testid="partners-section">
        <div className="container-custom mb-8">
          <motion.div className="text-center" {...fadeInUp}>
            <h2 className="font-outfit text-xl md:text-2xl font-bold text-[#000000] mb-1">
              Expertise multi-marques
            </h2>
            <p className="text-slate-600 text-sm">Daikin, Mitsubishi Electric, Atlantic, Toshiba et plus</p>
          </motion.div>
        </div>
        
        <div className="relative">
          <div className="flex marquee-track">
            {[...partners, ...partners].map((partner, index) => (
              <div 
                key={index}
                className="flex-shrink-0 px-6 lg:px-10 py-3"
              >
                <div className="bg-white px-6 py-4 rounded-xl shadow-sm h-20 w-40 flex items-center justify-center">
                  <img 
                    src={partner.logo}
                    alt={`Logo ${partner.name} - Partenaire ADM Confort Clim`}
                    className="max-h-12 max-w-full object-contain"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="testimonials-section">
        <div className="container-custom">
          <motion.div className="text-center mb-10" {...fadeInUp}>
            <div className="flex justify-center mb-4">
              <GoogleBadge />
            </div>
            <h2 className="font-outfit text-2xl md:text-3xl lg:text-4xl font-bold text-[#000000] mb-3">
              Avis clients vérifiés
            </h2>
            <p className="text-slate-600 text-sm md:text-base">
              La satisfaction de nos clients témoigne de notre sérieux.
            </p>
          </motion.div>

          {/* Desktop: Grid layout */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.slice(0, 6).map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-[#F8FAFC] p-5 rounded-xl"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-[#fabc63] fill-[#fabc63]" />
                  ))}
                </div>
                <p className="text-slate-600 mb-4 text-sm leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#1997b0] rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-xs">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-[#000000] text-sm">{testimonial.name}</div>
                    <div className="text-xs text-slate-500">{testimonial.location}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mobile: Horizontal Carousel */}
          <div className="md:hidden relative">
            <div className="overflow-hidden">
              <motion.div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className="w-full flex-shrink-0 px-2"
                  >
                    <div className="bg-[#F8FAFC] p-5 rounded-xl mx-auto max-w-sm">
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-[#fabc63] fill-[#fabc63]" />
                        ))}
                      </div>
                      <p className="text-slate-600 mb-4 text-sm leading-relaxed">"{testimonial.text}"</p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#1997b0] rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-xs">{testimonial.name[0]}</span>
                        </div>
                        <div>
                          <div className="font-semibold text-[#000000] text-sm">{testimonial.name}</div>
                          <div className="text-xs text-slate-500">{testimonial.location}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={prevTestimonial}
                className="w-9 h-9 rounded-full bg-white shadow flex items-center justify-center"
                data-testid="testimonial-prev"
              >
                <ChevronLeft size={18} className="text-slate-600" />
              </button>
              
              <div className="flex items-center gap-1.5">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      currentTestimonial === index 
                        ? 'bg-[#1997b0] w-4' 
                        : 'bg-slate-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextTestimonial}
                className="w-9 h-9 rounded-full bg-white shadow flex items-center justify-center"
                data-testid="testimonial-next"
              >
                <ChevronRight size={18} className="text-slate-600" />
              </button>
            </div>
          </div>

          {/* Google Reviews CTA */}
          <motion.div 
            className="mt-10 text-center"
            {...fadeInUp}
          >
            <a
              href="https://share.google/fFoF35oPbjq0fGiqs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-md hover:shadow-lg transition-all group border border-slate-100"
              data-testid="google-reviews-cta"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="font-medium text-slate-700 group-hover:text-[#1997b0] transition-colors">
                Consulter nos avis Google
              </span>
              <ArrowRight size={16} className="text-slate-400 group-hover:text-[#1997b0] group-hover:translate-x-1 transition-all" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Realisations Preview */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]" data-testid="realisations-preview-section">
        <div className="container-custom">
          <motion.div className="text-center mb-10" {...fadeInUp}>
            <span className="badge-primary mb-3">Nos réalisations</span>
            <h2 className="font-outfit text-2xl md:text-3xl lg:text-4xl font-bold text-[#000000] mt-3 mb-3">
              Nos réalisations en France
            </h2>
            <p className="text-slate-600 text-sm md:text-base">
              Climatisation, pompe à chaleur et froid commercial.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 mb-8">
            {realisations.map((realisation, index) => (
              <motion.div
                key={index}
                className="gallery-item aspect-[4/3]"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`realisation-preview-${index}`}
              >
                <img 
                  src={realisation.image}
                  alt={realisation.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link 
              to="/realisations"
              className="btn-secondary inline-flex items-center gap-2 text-sm"
              data-testid="realisations-cta"
            >
              Voir nos réalisations <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-16 lg:py-24 bg-white" data-testid="zones-preview-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="badge-primary mb-3">Zone d'intervention</span>
              <h2 className="font-outfit text-2xl md:text-3xl lg:text-4xl font-bold text-[#000000] mt-3 mb-4">
                Climaticien basé en Seine-et-Marne, intervention France entière
              </h2>
              <p className="text-slate-600 mb-6 text-sm md:text-base">
                Siège social à Moissy-Cramayel (77), nous intervenons en Île-de-France et dans toute la France selon les prestations demandées.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {["Moissy-Cramayel", "Melun", "Sénart", "Lieusaint", "Savigny-le-Temple", "Pontault-Combault"].map((city, index) => (
                  <div key={index} className="flex items-center gap-2 text-slate-700">
                    <MapPin className="text-[#1997b0]" size={14} />
                    <span className="text-sm">{city}</span>
                  </div>
                ))}
              </div>
              <Link 
                to="/zone-intervention"
                className="btn-secondary inline-flex items-center gap-2 text-sm"
                data-testid="zones-cta"
              >
                Voir toutes les villes <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div
              className="rounded-2xl overflow-hidden shadow-lg h-[300px] lg:h-[350px]"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d42012.95037044547!2d2.5549683!3d48.6287089!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e5e1a0e1b4d3a5%3A0x40799119fb1b8e0!2s77550%20Moissy-Cramayel!5e0!3m2!1sfr!2sfr!4v1702656000000!5m2!1sfr!2sfr"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Climaticien Moissy-Cramayel Seine-et-Marne"
              ></iframe>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]" data-testid="faq-section">
        <div className="container-custom">
          <motion.div className="text-center mb-10" {...fadeInUp}>
            <span className="badge-primary mb-3">FAQ</span>
            <h2 className="font-outfit text-2xl md:text-3xl lg:text-4xl font-bold text-[#000000] mt-3 mb-3">
              Questions fréquentes
            </h2>
          </motion.div>

          <motion.div className="max-w-2xl mx-auto" {...fadeInUp}>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl px-5 border-none shadow-sm"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-outfit font-semibold text-[#000000] hover:no-underline py-4 text-sm md:text-base">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4 text-sm">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="contact-section-home">
        <div className="container-custom">
          <motion.div className="text-center mb-10" {...fadeInUp}>
            <span className="badge-primary mb-3">Contact</span>
            <h2 className="font-outfit text-2xl md:text-3xl lg:text-4xl font-bold text-[#000000] mt-3 mb-3">
              Demandez un devis gratuit
            </h2>
            <p className="text-slate-600 text-sm md:text-base">
              Intervention rapide. Devis gratuit et sans engagement.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Contact Info Cards */}
            <motion.div className="space-y-3" {...fadeInUp}>
              <a 
                href="tel:0603054911"
                className="flex items-center gap-4 p-4 bg-[#F8FAFC] rounded-xl hover:shadow-md transition-all group"
                data-testid="home-contact-phone"
              >
                <div className="w-12 h-12 bg-[#fabc63] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="text-white" size={20} />
                </div>
                <div>
                  <p className="text-[#1997b0] font-bold text-lg">06 03 05 49 11</p>
                  <p className="text-xs text-[#fabc63] font-semibold">Urgences 24h/24</p>
                </div>
              </a>

              <a 
                href="mailto:admconfortclim@gmail.com"
                className="flex items-center gap-4 p-4 bg-[#F8FAFC] rounded-xl hover:shadow-md transition-all group"
                data-testid="home-contact-email"
              >
                <div className="w-12 h-12 bg-[#1997b0] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="text-white" size={20} />
                </div>
                <div>
                  <p className="text-[#1997b0] font-medium text-sm">admconfortclim@gmail.com</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 bg-[#F8FAFC] rounded-xl">
                <div className="w-12 h-12 bg-[#1997b0] rounded-full flex items-center justify-center">
                  <MapPin className="text-white" size={20} />
                </div>
                <div>
                  <p className="text-slate-600 text-sm">209 rue de Changis</p>
                  <p className="text-slate-600 text-sm">77550 Moissy-Cramayel</p>
                </div>
              </div>

              <div className="pt-2 space-y-2">
                <a 
                  href="tel:0603054911"
                  className="btn-emergency w-full flex items-center justify-center gap-2 text-sm !py-3"
                  data-testid="home-cta-urgent"
                >
                  <Phone size={16} />
                  Appeler maintenant
                </a>
                <Link 
                  to="/contact"
                  className="btn-primary w-full flex items-center justify-center gap-2 text-sm !py-3"
                  data-testid="home-cta-devis"
                >
                  <FileText size={16} />
                  Demander un devis
                </Link>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#F8FAFC] p-5 lg:p-6 rounded-xl">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="home-contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label text-xs">Nom *</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Votre nom"
                        required
                        className="form-input text-sm"
                        data-testid="home-input-name"
                      />
                    </div>
                    <div>
                      <label className="form-label text-xs">Téléphone *</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="06 00 00 00 00"
                        required
                        className="form-input text-sm"
                        data-testid="home-input-phone"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label text-xs">Email *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="votre@email.com"
                        required
                        className="form-input text-sm"
                        data-testid="home-input-email"
                      />
                    </div>
                    <div>
                      <label className="form-label text-xs">Service</label>
                      <Select onValueChange={handleServiceChange} value={formData.service_type}>
                        <SelectTrigger className="form-input text-sm" data-testid="home-select-service">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pose-climatisation">Pose climatisation</SelectItem>
                          <SelectItem value="depannage-climatisation">Dépannage climatisation</SelectItem>
                          <SelectItem value="entretien-climatisation">Entretien climatisation</SelectItem>
                          <SelectItem value="pompe-chaleur">Pompe à chaleur air/eau</SelectItem>
                          <SelectItem value="chambre-froide">Chambre froide</SelectItem>
                          <SelectItem value="ventilation">Ventilation / VMC</SelectItem>
                          <SelectItem value="depannage-urgent">Dépannage urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="form-label text-xs">Message *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value, subject: 'Demande de contact' }))}
                      placeholder="Décrivez votre projet..."
                      required
                      rows={3}
                      className="form-input resize-none text-sm"
                      data-testid="home-input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                    data-testid="home-contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Envoi...</span>
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        <span>Envoyer ma demande</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 lg:py-16 bg-[#1997b0]" data-testid="cta-section">
        <div className="container-custom">
          <motion.div 
            className="text-center text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-outfit text-2xl md:text-3xl font-bold mb-3">
              Besoin d'une intervention rapide ?
            </h2>
            <p className="text-white/80 mb-6 text-sm md:text-base">
              Climaticien disponible 24h/24 et 7j/7, intervention France entière.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a 
                href="tel:0603054911"
                className="btn-emergency flex items-center gap-2"
                data-testid="cta-call"
              >
                <Phone size={18} />
                <span>06 03 05 49 11</span>
              </a>
              <Link 
                to="/contact"
                className="bg-white text-[#1997b0] hover:bg-slate-100 transition-all duration-300 rounded-full px-6 py-3 font-semibold flex items-center gap-2 text-sm"
                data-testid="cta-contact"
              >
                <FileText size={18} />
                <span>Devis gratuit</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Floating CTA */}
      <div className="floating-cta">
        <a
          href="tel:0603054911"
          className="w-14 h-14 bg-[#fabc63] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          data-testid="floating-call"
          aria-label="Appeler maintenant"
        >
          <Phone className="text-white" size={24} />
        </a>
      </div>
    </div>
  );
};

export default HomePage;
